import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

const DEFAULT_FORM_STATE = {
  targetAdmin: null
};

/**
 * @typedef {object} TargetAdminDetails
 * @property {number} id
 * @property {string} name
 * @property {string} email
 */

/**
 * @typedef {object} PartnershipFormData
 * @property {number} targetAdmin - The ID of the selected partner
 */

/**
 * A modal for creating a new Upselling Partnership
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {function(PartnershipFormData): Promise<void>} props.onConfirm - Async function to call with form data when saving
 * @param {boolean} props.loading - Whether the form is currently submitting
 * @param {Array<TargetAdminDetails>} [props.availablePartnerships=[]] - List of available admins to partner with
 * @param {boolean} [props.availablePartnerships=false] - Whether the candidates list is loading
 */

const CreatePartnershipModal = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  availablePartnerships = [],
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  const isFormValid =
    formData.targetAdmin !== null;

  useEffect(() => {
    if (open) {
      setFormData(DEFAULT_FORM_STATE);
      setFormErrors({});
    }
  }, [open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handlePartnerChange = (event, newValue) => {
    setFormData((previous) => ({ ...previous, targetAdmin: newValue }));
    if (formErrors.target_admin) {
      setFormErrors((previous) => ({ ...previous, targetAdmin: null }));
    }
  };

  const valdiateForm = () => {
    const errors = {};
    if (!formData.targetAdmin) {
      errors.targetAdmin =
        "Debes seleccionar un Partner (Courier) para generar la alianza";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!valdiateForm()) return;

    const payload = {
      target_admin: formData.targetAdmin.id,
      permissions: {},
    };

    onConfirm(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nueva Alianza de Upselling</DialogTitle>

      <DialogContent sx={{width: "70%"}}>
        <Autocomplete
          id="targetAdmin"
          options={availablePartnerships}
          getOptionLabel={(option) => `${option.user.first_name} ${option.user.last_name}` || option.user.email || ""}
          value={formData.targetAdmin}
          onChange={handlePartnerChange}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              label="Seleccionar Courier / Partner"
              variant="outlined"
              error={!!formErrors.targetAdmin}
              helperText={formErrors.targetAdmin}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Crear Alianza"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePartnershipModal;
